// @flow

import React, { Component, Fragment } from "react";
import { map, get, bindAll, chunk, isEqual, noop } from "lodash";
import type {
  RaceTypeCodeEnum,
  Willpay,
  WillpayLegResult,
  WillpayPayout
} from "@tvg/types/Race";
import { formatCurrency } from "@tvg/formatter";

import TabGroup from "../../_molecule/TabGroup";
import MessageBox from "../../_molecule/MessageBox";
import RunnerNumber from "../../_atom/RunnerNumber";
import { Table, Tr, Th, Td, Runner, Payout } from "./styled-components";

type State = {
  selectedtab: number,
  selectedWillpay: Willpay
};

type Props = {
  /**
   * Willpays object
   */
  willpays: Array<Willpay> | null,
  /**
   * Race type code
   */
  raceTypeCode: RaceTypeCodeEnum,
  /**
   * Function call for gtm event after a tab has been click
   */
  handleGtmOnTabClick: Function
};

export const pushElementIfNotUndefined = (
  el: WillpayPayout,
  arr: Array<*>
): void => {
  if (el) {
    arr.push(el);
  }
};

export default class RaceWillpays extends Component<Props, State> {
  static defaultProps = {
    willpays: null,
    raceTypeCode: "T",
    handleGtmOnTabClick: noop
  };

  static getDerivedStateFromProps(props: Props, state: State) {
    if (!get(props, `willpays[${state.selectedtab}]`, null)) {
      return {
        selectedtab: 0,
        selectedWillpay: get(props, "willpays[0]", null)
      };
    }
    return {
      ...state,
      selectedWillpay: get(props, `willpays[${state.selectedtab}]`, null)
    };
  }

  constructor(props: Props) {
    super(props);

    this.state = {
      selectedtab: 0,
      selectedWillpay: get(this.props, "willpays[0]", null)
    };

    bindAll(this, "buildTabs", "handleTabClick", "renderResults");
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    return (
      get(nextProps, "willpays.length", 0) !==
        get(this.props, "willpays.length", 0) ||
      !isEqual(nextProps, this.props) ||
      !isEqual(nextState, this.state)
    );
  }

  buildTabs() {
    const tabs = map(
      get(this.props, "willpays", []),
      (willpay: Willpay, index: number) => ({
        title: willpay.type.name,
        isActive: this.state.selectedtab === index,
        onClick: () => {
          this.handleTabClick(index, willpay.type.name);
        }
      })
    );

    return tabs;
  }

  handleTabClick(newTabIndex: number, section: string) {
    this.props.handleGtmOnTabClick(section);
    if (this.state.selectedtab !== newTabIndex) {
      this.setState({
        selectedtab: newTabIndex,
        selectedWillpay: get(this.props, `willpays[${newTabIndex}]`, null)
      });
    }
  }

  renderResults() {
    return (
      this.state.selectedWillpay && (
        <thead>
          <Tr>
            <Th>
              <strong>
                {formatCurrency(this.state.selectedWillpay.wagerAmount)}
              </strong>
              &nbsp;
              {`${get(this.state.selectedWillpay, "type.name")} RESULTS: ${map(
                this.state.selectedWillpay.legResults,
                (result: WillpayLegResult) => result.winningBi
              ).join(" | ")} | *`}
            </Th>
          </Tr>
        </thead>
      )
    );
  }

  renderWillpays() {
    const inicialPayouts = get(this.state.selectedWillpay, "payouts", []);
    const resultPayouts = [];
    const circleLength = Math.ceil(inicialPayouts.length / 2);

    for (let i = 0; i < circleLength; i += 1) {
      pushElementIfNotUndefined(inicialPayouts[i], resultPayouts);
      pushElementIfNotUndefined(
        inicialPayouts[i + circleLength],
        resultPayouts
      );
    }

    const payouts = chunk(resultPayouts, 2);

    return (
      payouts.length && (
        <tbody>
          {map(payouts, (payoutCouple: Array<WillpayPayout>, index: number) => {
            const tdAttrs = payoutCouple.length === 1 ? { colSpan: 2 } : {};

            return (
              <Tr key={`payout-line-${index.toString()}`}>
                {map(payoutCouple, (payout: WillpayPayout) => (
                  <Td
                    key={`payout-runner-${payout.bettingInterestNumber}`}
                    {...tdAttrs}
                  >
                    <Runner>
                      <RunnerNumber
                        raceTypeCode={this.props.raceTypeCode}
                        runnerId={payout.bettingInterestNumber.toString()}
                      />
                    </Runner>
                    <Payout>
                      {payout.payoutAmount > 0
                        ? formatCurrency(payout.payoutAmount)
                        : "-"}
                    </Payout>
                  </Td>
                ))}
              </Tr>
            );
          })}
        </tbody>
      )
    );
  }

  render() {
    if (this.props.willpays) {
      return (
        <Fragment>
          <div>
            <TabGroup tabs={this.buildTabs()} stretch={false} />
          </div>
          <Table>
            {this.renderResults()}
            {this.renderWillpays()}
          </Table>
        </Fragment>
      );
    }

    return (
      <MessageBox
        type="info"
        message={
          <Fragment>
            Will Pays information isn't available yet
            <br />
            Please check back later
          </Fragment>
        }
        title="Will Pays Unavailable"
        isVertical
      />
    );
  }
}
